import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  BASE_END_POINT_COMPANY,
  BASE_END_POINT_JOB,
  JOB_END_POINT
} from '../../../config';
import { saveDraftJobSession } from '../../../lib/sessionStorage';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { STATUS_INACTIVE, USER_ID_JOB } from '../../../common/adapters';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import {
  clearUserJobs,
  createJobSucceeded,
  getEnrichedJobsSucceeded,
  getJobByIdSucceeded,
  getJobsByCurrentUserSucceeded,
  getJobsSucceeded
} from '../job/jobsSlice';
import { trackJobCreated } from '../../../helpers';

export const jobApi = createApi({
  reducerPath: 'jobApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_END_POINT_JOB}`
  }),
  tagTypes: ['Jobs'],
  endpoints: (build) => ({
    findJobsByCurrentUserId: build.query({
      query: ({ userId }) => {
        const localAuth = getLocalStorageAuthentication();
        const userIdEncoded = encodeURIComponent(userId);
        const url = `${JOB_END_POINT}/user/${userIdEncoded}`;

        console.log(
          '🌐 [Jobs API] Fetching jobs from:',
          `${BASE_END_POINT_JOB}${url}`
        );
        console.log(
          '🔑 [Jobs API] Using JWT token:',
          localAuth.token ? 'Present' : 'Missing'
        );
        console.log('👤 [Jobs API] User ID:', userId);

        return {
          url: url,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          }
        };
      },
      providesTags: (result, error, arg) =>
        result?.data ? [{ type: 'Jobs', id: arg.userId }] : [],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          console.log('✅ [Jobs API] Response received:', data);

          // Dispatch jobs to Redux store
          const jobs = data?.data;
          if (Array.isArray(jobs)) {
            console.log(
              '📋 [Jobs API] Dispatching',
              jobs.length,
              'jobs to Redux'
            );
            dispatch(getJobsByCurrentUserSucceeded({ jobs }));
          }
        } catch (error) {
          console.error('❌ [Jobs API] Failed to fetch jobs:', error);
        }
      }
    }),
    findJobById: build.query({
      query: ({ jobId }) => {
        console.log(
          '🌐 [Jobs API] Fetching job by ID from:',
          `${BASE_END_POINT_JOB}${JOB_END_POINT}/${jobId}`
        );
        console.log('🔍 [Jobs API] Job ID:', jobId);
        const localAuth = getLocalStorageAuthentication();
        return {
          url: `${JOB_END_POINT}/${jobId}`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          }
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          console.log('✅ [Jobs API] Job fetched successfully:', data);

          // Dispatch job to Redux store
          const job = data?.data;
          if (job && job.id) {
            console.log('📋 [Jobs API] Dispatching job to Redux');
            dispatch(getJobByIdSucceeded({ job }));
          }
        } catch (error) {
          console.error('❌ [Jobs API] Failed to fetch job:', error);
        }
      }
    }),
    findActiveJobs: build.query({
      query: ({ category, type } = {}) => {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (type) params.append('type', type);

        const queryString = params.toString();
        const url = `${JOB_END_POINT}/active${
          queryString ? `?${queryString}` : ''
        }`;

        console.log(
          '🌐 [Jobs API] Fetching active jobs from:',
          `${BASE_END_POINT_JOB}${url}`
        );
        if (category) console.log('📂 [Jobs API] Category filter:', category);
        if (type) console.log('🏷️ [Jobs API] Type filter:', type);

        return {
          url: url,
          method: 'GET'
        };
      },
      providesTags: ['Jobs'],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          console.log('✅ [Jobs API] Active jobs response received:', data);

          // Dispatch jobs to Redux store
          const jobs = data?.data;
          if (Array.isArray(jobs)) {
            console.log(
              '📋 [Jobs API] Dispatching',
              jobs.length,
              'active jobs to Redux'
            );
            dispatch(getJobsSucceeded({ jobs }));
          }
        } catch (error) {
          console.error('❌ [Jobs API] Failed to fetch active jobs:', error);
        }
      }
    }),
    searchJobs: build.query({
      query: ({ textToSearch = '' } = {}) => {
        const url = `/api/v1/job/search?textToSearch=${encodeURIComponent(
          textToSearch
        )}`;

        console.log(
          '🔍 [Jobs API] Searching jobs from gateway:',
          `${BASE_END_POINT_COMPANY}${url}`
        );
        console.log(
          '🔍 [Jobs API] Search text:',
          textToSearch || '(empty - all jobs)'
        );
        console.log(
          '🔍 [Jobs API] Public endpoint - no authentication headers'
        );

        // Public endpoint - no authentication required
        return {
          url: url,
          method: 'GET',
          baseUrl: BASE_END_POINT_COMPANY,
          headers: {}
        };
      },
      providesTags: ['Jobs'],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          console.log('✅ [Jobs API] Search results received:', data);

          // Dispatch enriched jobs to Redux store
          const jobResults = data?.data;
          if (Array.isArray(jobResults)) {
            console.log(
              '📋 [Jobs API] Dispatching',
              jobResults.length,
              'enriched jobs to Redux'
            );
            dispatch(getEnrichedJobsSucceeded({ jobResults }));
          }
        } catch (error) {
          console.error('❌ [Jobs API] Failed to search jobs:', error);
        }
      }
    }),
    findActiveJobsEnriched: build.query({
      query: ({ category, type } = {}) => {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (type) params.append('type', type);

        const queryString = params.toString();
        const url = `/api/v1/job/active${queryString ? `?${queryString}` : ''}`;

        console.log(
          '🔍 [Jobs API] Fetching enriched active jobs from gateway:',
          `${BASE_END_POINT_COMPANY}${url}`
        );
        if (category) console.log('📂 [Jobs API] Category filter:', category);
        if (type) console.log('🏷️ [Jobs API] Type filter:', type);
        console.log(
          '🔍 [Jobs API] Public endpoint - no authentication headers'
        );

        // Public endpoint - no authentication required
        return {
          url: url,
          method: 'GET',
          baseUrl: BASE_END_POINT_COMPANY,
          headers: {}
        };
      },
      providesTags: (result, error, arg) => [
        {
          type: 'Jobs',
          id: `active-${arg.category || 'all'}-${arg.type || 'all'}`
        }
      ],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          console.log(
            '✅ [Jobs API] Enriched active jobs response received:',
            data
          );

          // Dispatch enriched jobs to Redux store
          const jobResults = data?.data;
          if (Array.isArray(jobResults)) {
            console.log(
              '📋 [Jobs API] Dispatching',
              jobResults.length,
              'enriched active jobs to Redux'
            );
            dispatch(getEnrichedJobsSucceeded({ jobResults }));
          }
        } catch (error) {
          console.error(
            '❌ [Jobs API] Failed to fetch enriched active jobs:',
            error
          );
        }
      }
    }),
    createJob: build.mutation({
      query: ({ job }) => {
        const localAuth = getLocalStorageAuthentication();

        // Remove ID for new jobs to prevent accidental updates
        const newJob = { ...job };
        delete newJob.id;

        // Set the userId to the currently authenticated user
        newJob[USER_ID_JOB] = localAuth.userId;

        // Set default status to 'inactive' for new jobs (requires payment to activate)
        if (!newJob.status) {
          newJob.status = STATUS_INACTIVE;
        }

        saveDraftJobSession(newJob);

        return {
          url: `${JOB_END_POINT}`,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: newJob
        };
      },
      invalidatesTags: (result, error, arg) => {
        const localAuth = getLocalStorageAuthentication();
        return result?.data ? [{ type: 'Jobs', id: localAuth.userId }] : [];
      },
      // Effects to trigger on success
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'Job not created, please try again',
              severity: Severity.ERROR
            })
          );
        };
        try {
          const { data } = await queryFulfilled;
          // API now returns JobResult with { job, company, file } structure
          const jobResult = data?.data;
          const job = jobResult?.job;
          const isJobCreated = job && job.id;

          if (isJobCreated) {
            saveDraftJobSession(job);
            dispatch(createJobSucceeded(job));

            // Track job creation in Google Analytics
            trackJobCreated(job.id, job.title || 'Unknown', {
              job_category: job.category || 'Not specified',
              job_type: job.type || 'Not specified',
              has_benefits: !!(job.benefits && job.benefits.length > 0),
              has_regional_restrictions: !!job.regionalRestrictions,
              has_how_to_apply: !!job.howToApply,
              company_id: job.companyId || 'Not specified'
            });

            // Clear user jobs so dashboard will refetch on next visit
            dispatch(clearUserJobs());
            // Manually invalidate jobs cache to refresh dashboard
            const localAuth = getLocalStorageAuthentication();
            dispatch(jobApi.util.invalidateTags([{ type: 'Jobs', id: localAuth.userId }]));
            
            dispatch(
              messageUpdated({
                text: 'Job created successfully',
                severity: Severity.SUCCESS
              })
            );
          } else {
            errorMessage();
          }
        } catch {
          errorMessage();
        }
      }
    }),
    updateJob: build.mutation({
      query: ({ job }) => {
        const localAuth = getLocalStorageAuthentication();

        // Ensure the userId matches the authenticated user
        // The backend will validate this, but we prevent unnecessary API calls
        const updatedJob = { ...job };
        updatedJob[USER_ID_JOB] = localAuth.userId;

        saveDraftJobSession(updatedJob);

        return {
          url: `${JOB_END_POINT}/${updatedJob.id}`,
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: updatedJob
        };
      },
      invalidatesTags: (result, error, arg) =>
        result?.data ? [{ type: 'Jobs', id: arg.job.userId }] : [],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'Job not updated, please try again',
              severity: Severity.ERROR
            })
          );
        };
        try {
          const { data } = await queryFulfilled;
          // API now returns JobResult with { job, company, file } structure
          const jobResult = data?.data;
          const job = jobResult?.job;
          const isJobUpdated = job && job.id;

          if (isJobUpdated) {
            // Update the jobs from Redux store
            dispatch(clearUserJobs());
            dispatch(
              messageUpdated({
                text: 'Job updated successfully',
                severity: Severity.SUCCESS
              })
            );
          } else {
            errorMessage();
          }
        } catch {
          errorMessage();
        }
      }
    }),
    deleteJob: build.mutation({
      query: ({ jobId }) => {
        const localAuth = getLocalStorageAuthentication();
        return {
          url: `${JOB_END_POINT}/${jobId}`,
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          }
        };
      },
      invalidatesTags: (result, error, arg) => {
        const localAuth = getLocalStorageAuthentication();
        return result?.data ? [{ type: 'Jobs', id: localAuth.userId }] : [];
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'Job not deleted, please try again',
              severity: Severity.ERROR
            })
          );
        };
        try {
          const { data } = await queryFulfilled;
          // API now returns JobResult with { job, company, file } structure
          const jobResult = data?.data;
          const job = jobResult?.job;
          const isJobDeleted = job && job.id;

          if (isJobDeleted) {
            // Update the jobs from Redux store
            dispatch(clearUserJobs());
            dispatch(
              messageUpdated({
                text: 'Job deleted successfully',
                severity: Severity.SUCCESS
              })
            );
          } else {
            errorMessage();
          }
        } catch {
          errorMessage();
        }
      }
    })
  })
});
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFindJobsByCurrentUserIdQuery,
  useFindJobByIdQuery,
  useFindActiveJobsQuery,
  useFindActiveJobsEnrichedQuery,
  useSearchJobsQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  useDeleteJobMutation
} = jobApi;
